import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Users, UserPlus, Bot } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { useDashboard } from '../../features/dashboard/hooks/useDashboard';
import { StatsCard } from '../../features/dashboard/components/StatsCard';
import { ConversationsChart } from '../../features/dashboard/components/ConversationsChart';
import { ROUTES, getDomainDetailPath } from '../../config/routes';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getDomainDisplayStatus } from '../../lib/domainStatus';
import { dashboardService } from '../../services/dashboardService';
import { getTrialRemainingDays } from '../../lib/utils';

const relativeTime = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}時間前`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '昨日' : `${days}日前`;
};

const NOTIF_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  lead:    { label: 'リード', bg: '#F0FDFA', color: '#0a7a72' },
  sync:    { label: '学習',   bg: '#FFFBEB', color: '#D97706' },
  publish: { label: '公開',   bg: '#EEF2FF', color: '#4F46E5' },
};

export const DashboardPage: React.FC = () => {
  usePageTitle('ホーム');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [statsPeriod, setStatsPeriod] = useState<'month' | 'prev_month' | 'all'>('month');
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const { domains, domainsLoading, domainsFetched, subscription, notifications } = useDashboard();
  const statsQuery = useQuery({
    queryKey: ['dashboardStats', statsPeriod, selectedDomainId],
    queryFn: () => dashboardService.getStats(statsPeriod, selectedDomainId),
  });
  const stats = statsQuery.data?.data || null;

  const msgUsed = subscription?.usage?.messages_this_month ?? 0;
  const msgLimit = (subscription?.limits as any)?.monthly_messages ?? null;
  const msgPct = (msgLimit && msgLimit > 0) ? Math.min(100, Math.round((msgUsed / msgLimit) * 100)) : 0;
  const trialRemainingDays = getTrialRemainingDays(user?.trial_ends_at);
  const isTrial = user?.plan === 'trial';
  const isTrialActive = isTrial && !!user?.trial_ends_at && new Date(user.trial_ends_at) > new Date();
  const trialProgressPct = Math.round((trialRemainingDays / 14) * 100);
  const showEmptyState = domainsFetched && domains.length === 0;
  const activeCount = domains.filter((d: any) => getDomainDisplayStatus(d.status, d.sync_status) === 'active').length;
  const lastName = user?.name ? user.name.split(/[\s　]/)[0] : '';

  if (domainsLoading) {
    return <div data-testid="dashboard-skeleton"><div className="animate-pulse bg-gray-200 rounded-lg h-32" /></div>;
  }

  const TrialStrip = isTrialActive ? (
    <div data-testid="trial-banner" style={{ background: 'linear-gradient(90deg, #F0FDFA, #CCFBF1)', border: '1px solid #99F6E4', borderRadius: '12px', padding: '14px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#0F766E' }}>
        <span style={{ color: '#0a7a72', display: 'flex' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </span>
        <div>
          <div><b>トライアル残り {trialRemainingDays}日</b></div>
          <div style={{ width: '110px', height: '6px', background: '#99F6E4', borderRadius: '3px', overflow: 'hidden', marginTop: '5px' }}>
            <div style={{ height: '100%', background: '#0a7a72', borderRadius: '3px', width: `${trialProgressPct}%` }} />
          </div>
        </div>
      </div>
      <button
        data-testid="trial-banner-cta"
        onClick={() => navigate(ROUTES.SUBSCRIPTION)}
        style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
      >
        プランを見る
      </button>
    </div>
  ) : null;

  const NotificationBanner = (msgLimit != null && msgPct >= 80) ? (
    <div
      data-testid="notification-banner"
      style={{
        background: msgPct >= 100 ? 'linear-gradient(90deg, #FFF1F2, #FFE4E6)' : 'linear-gradient(90deg, #FFFBEB, #FEF3C7)',
        border: `1px solid ${msgPct >= 100 ? '#FCA5A5' : '#FCD34D'}`,
        borderRadius: '12px', padding: '14px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', gap: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: msgPct >= 100 ? '#B91C1C' : '#92400E' }}>
        <span style={{ display: 'flex', flexShrink: 0 }}>
          {msgPct >= 100 ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          )}
        </span>
        <span>
          {msgPct >= 100 ? '今月のメッセージ数が上限に達しました。' : `今月のメッセージ数が${msgPct}%に達しています。`}
        </span>
      </div>
      <Link
        to={ROUTES.SUBSCRIPTION}
        style={{ background: msgPct >= 100 ? '#DC2626' : '#D97706', color: '#fff', textDecoration: 'none', borderRadius: '8px', padding: '7px 16px', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}
      >
        プランを見る
      </Link>
    </div>
  ) : null;

  // BOT 0件
  if (showEmptyState) {
    return (
      <div data-testid="dashboard-page">
        {TrialStrip}
        {NotificationBanner}
        <div className="bg-white border border-[#E2E8F0] rounded-xl">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '64px 24px' }} className="sm:px-6">
            <div style={{ width: '96px', height: '96px', borderRadius: '24px', background: 'linear-gradient(135deg, #F0FDFA, #CCFBF1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0a7a72', marginBottom: '28px' }} className="w-20 h-20 sm:w-24 sm:h-24">
              <svg className="hidden sm:block" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>
              <svg className="sm:hidden" width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>
            </div>
            <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: '10px' }} className="text-[20px] sm:text-[22px]">
              <span className="hidden sm:inline">まだチャットボットがありません</span>
              <span className="sm:hidden">まだチャットボットが<br />ありません</span>
            </div>
            <div style={{ color: '#64748B', lineHeight: '1.7', marginBottom: '28px', maxWidth: '420px' }} className="text-[14px] sm:text-[15px]">
              <span className="hidden sm:inline">あなたのサイトのURLから、わずか3分でチャットボットが作れます。むずかしい設定はいりません。</span>
              <span className="sm:hidden">サイトのURLから、わずか3分で作れます。むずかしい設定はいりません。</span>
            </div>
            <button
              onClick={() => navigate(ROUTES.ONBOARDING)}
              data-testid="empty-state-create-bot"
              style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(10,122,114,0.25)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              className="px-7 py-4 text-base w-full sm:w-auto"
            >
              チャットボットを作る
              <svg className="hidden sm:inline" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" x2="19" y1="12" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // BOT 1件以上: 新ダッシュボード
  return (
    <div data-testid="dashboard-page">
      {TrialStrip}
      {NotificationBanner}

      {/* [A] ページタイトル */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A', margin: 0 }}>ホーム</h1>
        <p style={{ fontSize: '14px', color: '#64748B', marginTop: '4px' }}>
          こんにちは、{lastName}さん。チャットボットの状況をひと目で確認できます。
        </p>
      </div>

      {/* [B] 期間セレクタ + ドメインセレクタ + 統計カード4枚 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {([['all', '全期間'], ['month', '今月'], ['prev_month', '前月']] as const).map(([key, label]) => (
          <button key={key} onClick={() => setStatsPeriod(key)}
            style={{ padding: '6px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
              border: '1px solid', cursor: 'pointer',
              ...(statsPeriod === key
                ? { background: '#0a7a72', color: '#fff', borderColor: '#0a7a72' }
                : { background: '#fff', color: '#64748B', borderColor: '#E2E8F0' })
            }}>{label}</button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <select
            value={selectedDomainId ?? ''}
            onChange={(e) => setSelectedDomainId(e.target.value || null)}
            style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '13px', border: '1px solid #E2E8F0', background: '#fff', color: '#0F172A', cursor: 'pointer' }}
            data-testid="domain-selector"
          >
            <option value="">全体</option>
            {domains.map((d: any) => (
              <option key={d.id} value={d.id}>{d.description || d.domain}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="訪問者数"
          value={(stats?.visitors_count ?? 0).toLocaleString()}
          icon={<Users size={20} />}
        />
        <StatsCard
          title="チャット利用者数"
          value={(stats?.chat_users_count ?? 0).toLocaleString()}
          icon={<MessageSquare size={20} />}
        />
        <StatsCard
          title="獲得リード"
          value={((subscription?.usage as any)?.leads_this_month ?? 0).toLocaleString()}
          icon={<UserPlus size={20} />}
        />
        <StatsCard
          title="メッセージ数"
          value={statsPeriod !== 'all' && msgLimit && !selectedDomainId
            ? `${(stats?.messages_count ?? 0).toLocaleString()} / ${msgLimit.toLocaleString()}`
            : (stats?.messages_count ?? 0).toLocaleString()}
          icon={<Bot size={20} />}
        />
      </div>

      {/* [C] チャット利用者の推移 + クイックアクション */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <ConversationsChart domainId={selectedDomainId} />

        <div data-testid="quick-actions" className="bg-white rounded-card border border-border p-4">
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A', marginBottom: '12px' }}>クイックアクション</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button
              onClick={() => navigate(ROUTES.ONBOARDING)}
              style={{ background: '#0a7a72', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              新しいボットを作成
            </button>
            <button
              onClick={() => navigate(ROUTES.LEADS)}
              style={{ background: 'transparent', border: 'none', color: '#0a7a72', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              リードを確認
            </button>
            {domains[0] && (
              <button
                onClick={() => navigate(`${getDomainDetailPath(domains[0].id)}?tab=overview`)}
                style={{ background: 'transparent', border: 'none', color: '#0a7a72', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                埋め込みコードを取得
              </button>
            )}
          </div>
        </div>
      </div>

      {/* [D] 最近のアクティビティ + 最近のボット */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="recent-activity" className="bg-white rounded-card border border-border p-4">
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A', marginBottom: '12px' }}>最近のアクティビティ</p>
          {notifications.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#64748B' }}>アクティビティはありません</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {notifications.slice(0, 3).map((n: any) => {
                const badge = NOTIF_BADGE[n.type] || { label: n.type, bg: '#F1F5F9', color: '#64748B' };
                return (
                  <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ background: badge.bg, color: badge.color, fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', flexShrink: 0, lineHeight: '20px' }}>
                      {badge.label}
                    </span>
                    <span style={{ fontSize: '12px', color: '#0F172A', flex: 1 }}>{n.message || n.title}</span>
                    <span style={{ fontSize: '11px', color: '#64748B', whiteSpace: 'nowrap' }}>{relativeTime(n.created_at)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div data-testid="recent-bots" className="bg-white rounded-card border border-border p-4">
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#0F172A', marginBottom: '12px' }}>最近のボット</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {domains.slice(0, 3).map((d: any) => {
              const displayStatus = getDomainDisplayStatus(d.status, d.sync_status);
              const BADGE_MAP = {
                active:   { bg: '#F0FDFA', color: '#0a7a72',  label: '稼働中' },
                learning: { bg: '#FFFBEB', color: '#D97706',  label: '学習中' },
                inactive: { bg: '#F1F5F9', color: '#64748B',  label: '停止中' },
                error:    { bg: '#FEE2E2', color: '#991B1B',  label: 'エラー' },
              };
              const badge = BADGE_MAP[displayStatus];
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ background: badge.bg, color: badge.color, fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', flexShrink: 0, lineHeight: '20px' }}>
                    {badge.label}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#0F172A', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.description || d.domain}</span>
                  <span style={{ fontSize: '11px', color: '#64748B', flexShrink: 0 }}>{d.domain}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
